import {
  RX_SERIES_TYPES,
  RxSeries,
  RxSeriesType,
  RxDiagAction,
  RxEvent,
  RxEventType,
} from "./rx-diag-types";

type DataListState =
  | { status: "loading" }
  | { status: "loaded"; seriesTypes: RxSeriesType[]; eventsAvailable: boolean };

export type SeriesDataState = { filename: string; type: RxSeriesType; timeC: boolean } & (
  | { status: "loading" }
  | { status: "loaded"; series: RxSeries }
);

type EventsDataState = { filename: string; timeC: boolean } & (
  | { status: "loading" }
  | { status: "loaded"; events: RxEvent[] }
);

export type RxDiagState = {
  selectedFiles: string[]; // the files that the user has selected in the UI
  selectedSeriesTypes: RxSeriesType[]; // the series types the user has selected in the UI
  selectedEventTypes: RxEventType[]; // the event types the user has selected in the UI

  // status of first API call to see which datatypes exist for the given file, along with results
  dataListByFile: { [filename: string]: DataListState | undefined };

  // status of follow-up calls for each type of series data in flat format
  seriesData: SeriesDataState[];
  eventsData: EventsDataState[];
};

export const initialState: RxDiagState = {
  selectedFiles: [],
  selectedSeriesTypes: [],
  selectedEventTypes: [],

  dataListByFile: {},

  seriesData: [],
  eventsData: [],
};

export default function rxDiagReducer(
  state: RxDiagState = initialState,
  action: RxDiagAction
): RxDiagState {
  switch (action.type) {
    case "RXDIAG_SELECT_FILES":
      return { ...state, selectedFiles: action.payload.filenames };
    case "RXDIAG_SELECT_SERIES_TYPES":
      return { ...state, selectedSeriesTypes: action.payload.seriesTypes };
    case "RXDIAG_SELECT_EVENT_TYPES":
      return { ...state, selectedEventTypes: action.payload.eventTypes };

    case "RXDIAG_DATALIST_LOADING": {
      return {
        ...state,
        dataListByFile: {
          ...state.dataListByFile,
          [action.payload.filename]: { status: "loading" },
        },
      };
    }

    case "RXDIAG_DATALIST_LOADED": {
      const { filename, dataTypes } = action.payload;
      const eventsAvailable = dataTypes.includes("events");
      const seriesTypes = RX_SERIES_TYPES.filter(type => dataTypes.includes(type));

      return {
        ...state,
        dataListByFile: {
          ...state.dataListByFile,
          [filename]: { status: "loaded", seriesTypes, eventsAvailable },
        },
      };
    }

    case "RXDIAG_SERIES_LOADING": {
      const { filename, seriesType, timeC: timeCorrected } = action.payload;

      const seriesData = state.seriesData
        .filter(
          status =>
            status.filename !== filename ||
            status.type !== seriesType ||
            status.timeC !== timeCorrected
        )
        .concat({ filename, type: seriesType, timeC: timeCorrected, status: "loading" });

      return { ...state, seriesData };
    }

    case "RXDIAG_SERIES_LOADED": {
      const { series, timeC: timeCorrected } = action.payload;
      const { filename, type } = series;

      const seriesData = state.seriesData
        .filter(
          status =>
            status.filename !== filename || status.type !== type || status.timeC !== timeCorrected
        )
        .concat({ filename, type, timeC: timeCorrected, status: "loaded", series });

      return { ...state, seriesData };
    }

    case "RXDIAG_EVENTS_LOADING": {
      const { filename, timeC: timeCorrected } = action.payload;
      const eventsData = state.eventsData
        .filter(status => !(status.filename === filename && status.timeC === timeCorrected))
        .concat({ filename, timeC: timeCorrected, status: "loading" });

      return { ...state, eventsData };
    }

    case "RXDIAG_EVENTS_LOADED": {
      const { filename, events, timeC: timeCorrected } = action.payload;

      const fullEvents = events.map(event => ({ filename, ...event }));

      const eventsData = state.eventsData
        .filter(status => !(status.filename === filename && status.timeC === timeCorrected))
        .concat({ filename, timeC: timeCorrected, status: "loaded", events: fullEvents });

      return { ...state, eventsData };
    }

    default:
      return state;
  }
}
